import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import useSWR from 'swr';
import { useParams, useNavigate } from 'react-router-dom';
import { authenticatedFetcher } from '@/lib/swr';
import { useI18n } from '@/i18n/I18nProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Spinner from '@/components/ui/spinner';
import DocumentPicker, { DocumentT } from '@/components/DocumentPicker';
import StudentProfileCard from '../components/StudentProfileCard';
import type { StudentDocumentsResponse } from '@/types/document';
import { ApiResponse } from '@/types/api-response';
import { CaretLeftIcon, CaretRightIcon, WarningCircleIcon } from '@/components/icons';
import { useToastNotifications } from '@/lib/hooks/use-toast-notifications';
import { useGetStudentById } from '@/lib/hooks/useGetStudentById';
import { StatusCard } from '@/components/StatusCard';
import { PP_BASE_URL } from '@/lib/env';

export default function UpdateDocumentsPage() {
  const params = useParams<{ id: string; appId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n() as any;
  const notify = useToastNotifications();
  const sourcedId = params.id;
  const applicationId = params.appId;

  const [isQuestionsCompleted, setIsQuestionsCompleted] = React.useState(!!applicationId);
  const [parentCategory, setParentCategory] = React.useState<'' | 'OTHER' | 'MOE' | 'IMAM'>('');
  const [selectedFiles, setSelectedFiles] = React.useState<Record<number, DocumentT | null>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleFileSelect = (docTypeId: number, file: DocumentT | null) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [docTypeId]: file,
    }));
  };

  const { student, error: studentError, isLoading: studentLoading } = useGetStudentById(sourcedId);

  const ppBaseUrl = PP_BASE_URL;

  const queryParams = new URLSearchParams();
  if (sourcedId) {
    queryParams.set('studentSourceId', sourcedId);
  }
  if (parentCategory) {
    queryParams.set('parentCategory', parentCategory);
  }
  if (applicationId) {
    queryParams.set('applicationId', applicationId);
  }
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

  const documentsSwrKey =
    sourcedId && isQuestionsCompleted && (parentCategory || applicationId) && ppBaseUrl
      ? `${ppBaseUrl}/students/${sourcedId}/documents${queryString}`
      : null;

  const {
    data: documentsData,
    error: documentsError,
    isLoading: documentsLoading,
  } = useSWR<ApiResponse<StudentDocumentsResponse>>(documentsSwrKey, authenticatedFetcher, {
    shouldRetryOnError: (error) => {
      return error.status < 400 || error.status >= 500;
    },
  });

  const documentsResponse = documentsData?.data ?? (documentsData as StudentDocumentsResponse);

  const applicableDocuments = useMemo(
    () => documentsResponse?.applicableDocuments || [],
    [documentsResponse?.applicableDocuments]
  );
  const requiredDocuments = useMemo(
    () => applicableDocuments?.filter((doc) => doc.isRequired) || [],
    [applicableDocuments]
  );
  const additionalDocuments = useMemo(
    () => applicableDocuments?.filter((doc) => !doc.isRequired) || [],
    [applicableDocuments]
  );

  const lastUsedDocumentsMap = useMemo(() => {
    if (!documentsResponse?.parentDocuments || documentsResponse.parentDocuments.length === 0) {
      return {};
    }
    const map: Record<number, DocumentT> = {};
    documentsResponse.parentDocuments.forEach((e) => {
      map[e.documentTypeId] = {
        id: e.id,
        documentTypeId: e.documentTypeId,
        name: e.fileName,
        url: e.fileUrl,
      };
    });
    return map;
  }, [documentsResponse?.parentDocuments]);

  const submittedDocumentsDto = useMemo(() => {
    if (!documentsResponse?.submittedDocuments || documentsResponse.submittedDocuments.length === 0) {
      return {};
    }
    const map: Record<number, DocumentT> = {};
    documentsResponse.submittedDocuments.forEach((e) => {
      map[e.documentTypeId] = {
        id: e.id,
        documentTypeId: e.documentTypeId,
        name: lastUsedDocumentsMap[e.documentTypeId]?.name ?? e.fileName,
        url: e.fileUrl,
        status: e.status,
        comment: e.comment,
      };
    });
    return map;
  }, [documentsResponse?.submittedDocuments, lastUsedDocumentsMap]);

  React.useEffect(() => {
    if (!documentsResponse?.submittedDocuments || documentsResponse?.submittedDocuments.length === 0) {
      return;
    }
    const alreadySelectedFiles = documentsResponse?.submittedDocuments.reduce<Record<number, DocumentT>>((prev, e) => {
      return {
        ...prev,
        [e.documentTypeId]: { id: e.parentDocumentId, file: null } as DocumentT,
      };
    }, {});
    setSelectedFiles((prev) => ({ ...prev, ...alreadySelectedFiles }));
  }, [documentsResponse?.submittedDocuments]);

  if (studentLoading || documentsLoading) {
    return <Spinner />;
  }

  if (studentError || !student) {
    return <StatusCard message={studentError?.title} description={t.child.no_data_available_for_child} />;
  }

  if (documentsError) {
    const errorMessage =
      documentsError?.title ||
      documentsError?.error ||
      documentsError?.message ||
      (documentsError?.errors ? Object.values(documentsError.errors).flat().join(', ') : null) ||
      String(documentsError);
    return <StatusCard variant="error" message={errorMessage} />;
  }

  const displayName =
    locale === 'ar'
      ? [student.firstNameArabic, student.middleNameArabic, student.lastNameArabic].filter(Boolean).join(' ')
      : [
          student.firstNameEnglish,
          student.middleNameEnglish,
          student.thirdNameEnglish,
          student.fourthNameEnglish,
          student.familyNameEnglish,
        ]
          .filter(Boolean)
          .join(' ');

  const handleSubmitDocuments = async () => {
    if (!sourcedId) return;

    const missingRequired = requiredDocuments.filter((doc) => {
      const selected = selectedFiles[doc.id];
      return !selected;
    });

    if (missingRequired.length > 0) {
      notify.error(t?.fillRequiredDocumentsError);
      return;
    }

    const formData = new FormData();
    formData.append('StudentSourceId', sourcedId);
    if (parentCategory) {
      formData.append('ParentCategory', parentCategory);
    }
    if (applicationId) {
      formData.append('ApplicationId', applicationId);
    }

    let docIndex = 0;
    for (const doc of applicableDocuments) {
      const selected = selectedFiles[doc.id];
      if (!selected) continue;

      if (selected.id && !selected.file) {
        if (selected.id) {
          formData.append(`Documents[${docIndex}].ParentDocumentId`, String(selected.id));
          docIndex++;
        }
      } else if (selected.file) {
        formData.append(`Documents[${docIndex}].DocumentTypeId`, String(doc.id));
        formData.append(`Documents[${docIndex}].File`, selected.file);
        docIndex++;
      }
    }

    if (docIndex === 0) {
      notify.error(t?.fillRequiredDocumentsError);
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = `${ppBaseUrl}/students/${sourcedId}/documents`;
      await authenticatedFetcher<ApiResponse<object>>(endpoint, {
        method: 'POST',
        body: formData,
      });

      notify.success(t?.documentsUpdateSuccess);
      navigate('/dashboard');
    } catch (err: any) {
      const errorMsg =
        err?.title ||
        err?.message ||
        err?.error ||
        (err?.errors ? Object.values(err.errors).flat().join(', ') : null) ||
        (err?.message === 'Failed to fetch' ? t?.documentsUpdateFailed : String(err));
      notify.error(errorMsg);
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={clsx(
        'bg-gradient-to-br from-background/40 via-background to-background/60 relative',
        locale === 'ar' && 'direction-rtl'
      )}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-20 -left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '4s' }}
        />
        <div
          className="absolute top-40 -right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '6s', animationDelay: '1s' }}
        />
        <div
          className="absolute bottom-20 left-1/3 w-80 h-80 bg-primary/4 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '5s', animationDelay: '2s' }}
        />
      </div>

      <header className="" role="banner">
        <div className="">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: locale === 'ar' ? 'الرئيسية' : 'Home', href: '/dashboard' },
              { label: locale === 'ar' ? 'عرض الملف' : 'View Profile', href: `/students/${sourcedId}` },
              { label: locale === 'ar' ? 'تحديث المستندات' : 'Update Documents' },
            ]}
          />
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 py-4">
            <div className="flex-1">
              <h1
                className={clsx(
                  'text-xl sm:text-2xl lg:text-3xl font-bold text-foreground',
                  locale === 'ar' && 'text-right'
                )}
              >
                {locale === 'ar' ? 'تحديث المستندات' : 'Update Documents'}
              </h1>
              <p
                className={clsx(
                  'text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl',
                  locale === 'ar' && 'text-right'
                )}
              >
                {locale === 'ar' ? 'تحديث مستندات الطالب' : 'Update student documents'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main id="main-form" className="py-4" role="main">
        {/* Student Info Card */}
        <StudentProfileCard student={student} displayName={displayName} sourcedId={sourcedId} />

        {!isQuestionsCompleted ? (
          <Card className="border border-primary/20 shadow-sm hover:border-primary/40 mb-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-base sm:text-lg">{t?.additionalInformation}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3">
                <WarningCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <h5 className="font-semibold text-sm sm:text-base text-amber-950 dark:text-amber-100">
                    {t?.parentCategoryCautionTitle}
                  </h5>
                  <p className="text-xs sm:text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
                    {t?.parentCategoryCautionText}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 aegov-form-control">
                <div className="md:col-span-3">
                  <label htmlFor="parentCategorySelect">
                    {t?.parentCategoryLabel} <span className="text-destructive">*</span>
                  </label>
                </div>

                <div className="md:col-span-4 form-control-input">
                  <select
                    id="parentCategorySelect"
                    required
                    value={parentCategory}
                    onChange={(e) => setParentCategory(e.target.value as '' | 'OTHER' | 'MOE' | 'IMAM')}
                  >
                    <option value="" disabled hidden>
                      {t?.selectCategory}
                    </option>
                    <option value="IMAM">{t?.imamMosque}</option>
                    <option value="MOE">{t?.moeEmployee}</option>
                    <option value="OTHER">{t?.other}</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-primary/20 shadow-sm hover:border-primary/40 mb-4">
            <CardHeader className="pb-4">
              {/* Required Documents Section */}
              <CardTitle className="text-base sm:text-lg flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t?.requiredDocuments}</h3>
              </CardTitle>
              <CardContent>
                {requiredDocuments.length === 0 ? (
                  <p className="text-sm text-muted-foreground pb-4">{t?.noRequiredDocuments}</p>
                ) : (
                  requiredDocuments.map((doc) => {
                    const submittedDoc = submittedDocumentsDto[doc.id] ?? null;
                    return (
                      <DocumentPicker
                        key={doc.id}
                        documentType={{
                          id: doc.id,
                          name: locale === 'ar' ? doc.nameAr : doc.nameEn,
                          description: (locale === 'ar' ? doc.hintAr : doc.hintEn) ?? '',
                          required: true,
                          acceptedFormats: doc.acceptedFileFormats!,
                          maxFileSize: doc.maxFileSize ? Number(doc.maxFileSize) : undefined,
                        }}
                        lastUsed={lastUsedDocumentsMap[doc.id] ?? null}
                        submitted={submittedDoc}
                        isReadOnly={submittedDoc?.status === 3}
                        renderStatus={() => (
                          <>
                            {submittedDoc?.status === 2 && (
                              <p className="text-red-600 text-sm font-medium mt-1">{submittedDoc?.comment}</p>
                            )}
                            {submittedDoc?.status === 3 && (
                              <p className="text-green-600 text-sm font-medium mt-1">{t.approved}</p>
                            )}
                          </>
                        )}
                        onChange={(file) => handleFileSelect(doc.id, file)}
                      />
                    );
                  })
                )}
              </CardContent>

              {/* Additional Documents Section */}
              <CardTitle className="text-base sm:text-lg flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{t?.additionalDocuments}</h3>
              </CardTitle>
              <CardContent>
                {additionalDocuments.length === 0 ? (
                  <p className="text-sm text-muted-foreground pb-4">{t?.noAdditionalDocuments}</p>
                ) : (
                  additionalDocuments.map((doc) => {
                    const submittedDoc = submittedDocumentsDto[doc.id] ?? null;
                    return (
                      <DocumentPicker
                        key={doc.id}
                        documentType={{
                          id: doc.id,
                          name: locale === 'ar' ? doc.nameAr : doc.nameEn,
                          description: (locale === 'ar' ? doc.hintAr : doc.hintEn) ?? '',
                          required: false,
                          acceptedFormats: doc.acceptedFileFormats!,
                          maxFileSize: doc.maxFileSize ? Number(doc.maxFileSize) : undefined,
                        }}
                        lastUsed={lastUsedDocumentsMap[doc.id] ?? null}
                        submitted={submittedDoc}
                        isReadOnly={submittedDoc?.status === 3}
                        renderStatus={() => (
                          <>
                            {submittedDoc?.status === 2 && (
                              <p className="text-red-600 text-sm font-medium mt-1">{submittedDoc?.comment}</p>
                            )}
                            {submittedDoc?.status === 3 && (
                              <p className="text-green-600 text-sm font-medium mt-1">{t.approved}</p>
                            )}
                          </>
                        )}
                        onChange={(file) => handleFileSelect(doc.id, file)}
                      />
                    );
                  })
                )}
              </CardContent>
            </CardHeader>
          </Card>
        )}

        {/* Action Buttons - Positioned outside cards at the bottom */}
        <div
          className={clsx(
            'flex items-center gap-4 pt-6 border-t mt-8',
            isQuestionsCompleted && !applicationId ? 'justify-between' : 'justify-end'
          )}
        >
          {isQuestionsCompleted && !applicationId && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => setIsQuestionsCompleted(false)}
              className="aegov-btn btn-soft btn-secondary btn-sm"
            >
              {locale === 'ar' ? <CaretRightIcon size={20} /> : <CaretLeftIcon size={20} />}
              {t?.back}
            </button>
          )}
          <button
            type="button"
            disabled={isSubmitting || (!isQuestionsCompleted ? !parentCategory : false)}
            onClick={() => {
              if (!isQuestionsCompleted) {
                if (parentCategory) {
                  setIsQuestionsCompleted(true);
                }
              } else {
                handleSubmitDocuments();
              }
            }}
            className="aegov-btn btn-sm"
          >
            {isQuestionsCompleted ? t?.submit : t?.continue}
            {locale === 'ar' ? <CaretLeftIcon size={20} /> : <CaretRightIcon size={20} />}
          </button>
        </div>
      </main>
    </div>
  );
}
